import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router';
import { SIGN_IN_ACTION, signIn } from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import { InputPassword } from '../../../commonComponents';
import { Button } from '@tempmee/react';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { TurnstileLoginEnabled } from '~/constants/ff.posthog';
import { validateEmail } from '../../../commonComponents/CommonFunctions';
import { Colors } from '../../../themes/colors';
import InputError from '../components/InputError';
import { useTrackEvent } from '../../../hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';

// Turnstile site key from environment
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export default function SignIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Check if Turnstile is enabled via feature flag. Default to enabled so a
  // not-yet-resolved PostHog flag (cold load / slow network) can't drop
  // enforcement and submit a tokenless login. Rollout is 100%. GOTU-4317.
  const turnstileEnabled = useFeatureFlag(TurnstileLoginEnabled, true);

  const loadingSelector = createLoadingSelector([SIGN_IN_ACTION]);
  const isLoading = useSelector((state) => loadingSelector(state));
  const authError = useSelector((state) => state?.error?.[SIGN_IN_ACTION]);

  const showEmailError = !validateEmail(email) && email.length > 0;
  const disabledSubmit =
    email.indexOf(' ') >= 0 ||
    email.length <= 0 ||
    password.indexOf(' ') >= 0 ||
    password.length <= 0 ||
    (turnstileEnabled && !turnstileToken && !window.Cypress); // Disable if Turnstile enabled but no token (except in Cypress)

  useEffect(() => {
    // Log error if Turnstile is enabled but key is missing
    if (turnstileEnabled && !TURNSTILE_SITE_KEY) {
      console.error(
        '[Turnstile] ERROR: VITE_TURNSTILE_SITE_KEY environment variable is not set!',
      );
    }

    // Only initialize Turnstile if feature flag is enabled and site key is configured
    // Skip initialization for Cypress tests
    if (!turnstileEnabled || !TURNSTILE_SITE_KEY || window.Cypress) {
      return;
    }

    // Initialize Turnstile widget when component mounts
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: 'flexible', // Adapts to container width
            appearance: 'always', // Always show the widget
            theme: 'light', // Light theme to match login form
            callback: (token) => {
              setTurnstileToken(token);
              setTurnstileError(false);
            },
            'error-callback': () => {
              setTurnstileToken(null);
              setTurnstileError(true);
            },
            'expired-callback': () => {
              setTurnstileToken(null);
            },
          });
        } catch {
          setTurnstileError(true);
        }
      }
    };

    let checkInterval;
    let timeoutId;

    // Check if Turnstile is already loaded
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Wait for Turnstile to load with a timeout
      checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          initTurnstile();
        }
      }, 100);

      // Timeout after 10 seconds
      timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        // Don't set error - allow login without Turnstile if script fails to load
      }, 10000);
    }

    // Cleanup on unmount
    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [turnstileEnabled, TURNSTILE_SITE_KEY]);

  const resetTurnstile = () => {
    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (error) {
        console.error('Failed to reset Turnstile:', error);
      }
    }
    setTurnstileToken(null);
  };

  const submitSignIn = async (e) => {
    e.preventDefault();

    if (showEmailError) {
      setSubmitError(true);
      return;
    }

    // For Cypress tests, use a dummy token that backend accepts, only works if backend
    // is in a lower environment on the test secret keys
    if (window.Cypress) {
      dispatch(signIn({ email, password, turnstileToken: 'XXXX.DUMMY.TOKEN.XXXX' }));
      return;
    }

    // If Turnstile is disabled, submit without token
    if (!turnstileEnabled) {
      dispatch(signIn({ email, password }));
      return;
    }

    // If Turnstile is enabled, we need a token
    if (!turnstileToken) {
      setTurnstileError(true);
      return;
    }

    // Submit with token
    dispatch(signIn({ email, password, turnstileToken }));
  };

  // Handle sign-in failure - automatically reset Turnstile for a fresh token
  useEffect(() => {
    if (authError && turnstileEnabled && !window.Cypress) {
      // Reset the widget to get a fresh token for retry
      resetTurnstile();
    }
  }, [authError, turnstileEnabled]);

  return (
    <motion.form
      id="signinForm"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        width: '50%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        alignSelf: 'center',
        marginTop: 20,
      }}
    >
      <h1 className="global_font bold" style={{ color: Colors.primary_500, marginBottom: 0 }}>
        Hello Again!
      </h1>

      <p className="global_font f-dark" style={{ margin: 0 }}>
        Welcome Back! Please enter your details.
      </p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 430,
          marginBottom: 15,
        }}
      >
        <div style={{ position: 'relative', width: '100%', marginBottom: 20 }}>
          <p className="input_names" style={{ width: '100%', marginBottom: 8 }}>
            Email
          </p>
          <input
            className="input-field"
            data-cy="input-email"
            id="email"
            placeholder="Enter your email"
            style={{ width: 'calc(100% - 40px)', margin: 0 }}
            type="text"
            onChange={(e) => setEmail(e.target.value)}
          />
          <InputError
            text="The email address entered is invalid."
            showError={submitError && showEmailError}
          />
        </div>

        <p className="input_names" style={{ width: '100%', marginBottom: 8 }}>
          Password
        </p>
        <InputPassword
          dataCy="input-password"
          id="password"
          placeholder="Enter your password"
          style={{ width: 'calc(100% - 40px)' }}
          onChange={(e) => setPassword(e.target.value)}
        />

        {/* Turnstile Widget Container - only render if feature flag is enabled and not in Cypress */}
        {turnstileEnabled && TURNSTILE_SITE_KEY && !window.Cypress && (
          <>
            <div
              ref={turnstileRef}
              style={{
                marginTop: 20,
                display: 'flex',
                justifyContent: 'center',
              }}
              data-cy="turnstile-widget"
            ></div>
            {turnstileError && (
              <p style={{ fontSize: 14, marginTop: 8, textAlign: 'center' }}>
                <span style={{ color: 'red' }}>Verification required.</span>{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setTurnstileError(false);
                    resetTurnstile();
                  }}
                  style={{ color: '#0066cc', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Please try again
                </a>
              </p>
            )}
          </>
        )}
      </div>

      <h5
        className="sm-links"
        data-cy="forgot-password-link"
        id="forgotPasswordButton"
        onClick={() => {
          trackEvent(POSTHOG_EVENTS.OFFICE_LOGIN_FORGOT_PASSWORD_CLICKED);
          navigate('/forgotpassword');
        }}
      >
        Forgot Password?
      </h5>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          marginBottom: 120,
        }}
      >
        <Button
          data-cy="btn-log-in"
          isLoading={isLoading}
          disabled={disabledSubmit}
          type="submit"
          onClick={submitSignIn}
          size="lg"
        >
          Log In
        </Button>
      </div>
    </motion.form>
  );
}
